// bottom_editor: the thread's reply composer becomes a floating pill pinned to
// the bottom of the panel, Slack-style. The thread scrolls underneath it and
// dissolves into the card background just above it, so the composer is
// reachable at every scroll position instead of only at the end of the thread.
//
// It is on for everyone. The check stays the one place that asks, so the
// in-flow composer it replaces can be taken out behind it.
export function bottomEditorEnabled(): boolean {
    return true
}

// On a phone the pill sits on the panel's bottom edge, so it has to clear
// whatever the device draws there. The native shell reports the home indicator
// as --native-safe-area-bottom, Android reports its system controls, and a PWA
// only has env(); a notched iPhone in standalone mode under-reports through
// env() alone, so it gets the same allowance the navbar makes.
export const BOTTOM_EDITOR_MOBILE_EDGE_GAP_PX = 14

// Which of the panel's bottom controls a mounted pill displaces. Desktop
// threads leave the whole foot to the pill: the New button and the navbar both
// go. A phone keeps its navbar — it is the app's only navigation there — and the
// pill stands on top of it; only the New button, which would sit in the pill's
// corner, goes.
//
// The keyboard hides the phone's navbar, but under the pill only the keyboard
// itself does: elsewhere a press on a reply field hides the navbar at once, as a
// bridge until the keyboard's own state arrives (bottomNavigationHidden carries
// that). Under the pill that bridge opened a gap where the navbar had been, and
// the pill dropped into it only when the keyboard landed — two steps a beat
// apart. The navbar leaving and the pill's foot taking its place are one step
// now, at the keyboard's arrival.
export function resolveBottomControlsUnderEditor({
    isMobile = false,
    bottomNavigationHidden = false,
    keyboardOpen = false,
    bottomEditorMounted = false,
}: {
    isMobile?: boolean
    bottomNavigationHidden?: boolean
    keyboardOpen?: boolean
    bottomEditorMounted?: boolean
} = {}): { newButtonHidden: boolean; navigationHidden: boolean } {
    const phonePill = bottomEditorMounted && isMobile
    return {
        newButtonHidden: bottomNavigationHidden || bottomEditorMounted,
        navigationHidden: phonePill
            ? keyboardOpen
            : bottomNavigationHidden || (bottomEditorMounted && !isMobile),
    }
}

// On a phone the pill stands on the navbar, so the panel's foot has to clear
// it. PanelContainer measures the navbar's top edge against the panel's bottom
// and writes the clearance here; the spacer reads it, falling back to the bare
// device inset while the navbar is away.
export const BOTTOM_EDITOR_NAVIGATION_CLEARANCE_VAR = '--bottom-editor-navigation-clearance'

export function resolveBottomEditorInset({
    isIOSStandalone = false,
    isiOSWithHomeIndicator = false,
}: {
    isIOSStandalone?: boolean
    isiOSWithHomeIndicator?: boolean
} = {}): string {
    const browserInset =
        isIOSStandalone && isiOSWithHomeIndicator
            ? 'calc(env(safe-area-inset-bottom, 0px) + 30px)'
            : 'env(safe-area-inset-bottom, 0px)'
    return `calc(${BOTTOM_EDITOR_MOBILE_EDGE_GAP_PX}px + max(var(--native-safe-area-bottom, 0px), var(--native-system-controls-bottom, 0px), ${browserInset}))`
}

// A long reply stops the pill below the panel's top controls (the floating
// filter and the gap under it): past that, the field scrolls inside the pill.
// PanelContainer measures the room from the pill's foot up to that line and
// writes what is left for the field once the rest of the pill (a quote, the
// toolbar, previews) is counted, in the panel's layout px. Never less than two
// lines, however little room a landscape keyboard leaves.
export const BOTTOM_EDITOR_FIELD_MAX_HEIGHT_VAR = '--bottom-editor-field-max-height'
export const BOTTOM_EDITOR_FIELD_MIN_HEIGHT_PX = 48

export function resolveBottomEditorFieldMaxHeight({
    pillBottom,
    rest,
    topLimit,
    scale = 1,
}: {
    // The pill's foot, as drawn (viewport px).
    pillBottom: number
    // The rest of the pill: all of it but its field (viewport px).
    rest: number
    // The highest the pill's top may reach (viewport px).
    topLimit: number
    // A side panel's scale: viewport px per layout px.
    scale?: number
}): number {
    const room = pillBottom - topLimit - rest
    return Math.max(BOTTOM_EDITOR_FIELD_MIN_HEIGHT_PX, Math.floor(room / (scale > 0 ? scale : 1)))
}

// The rest of the pill is measured open and at rest. Folded, the toolbar is
// away and the rest measures short; mid-resize (the composer's fold motion)
// the pill is between sizes. Either would cap the field too tall, so the last
// open measure stands meanwhile, while the room is still followed.
export function canMeasureBottomEditorField({
    folded,
    resizing,
}: {
    folded: boolean
    resizing: boolean
}): boolean {
    return !folded && !resizing
}

// The composer's fold motion: an animation of its height. The padding
// transition that runs alongside moves the rest only a few px, and smoothly.
export function isBottomEditorResize(animation: Animation): boolean {
    try {
        const effect = animation.effect as KeyframeEffect | null
        return effect?.getKeyframes?.().some(frame => 'height' in frame) ?? false
    } catch {
        return false
    }
}

// Scrolls the pill's field, and only the field, so the caret shows inside it,
// a few px clear of its edges.
export function revealBottomEditorCaret(
    field: Pick<
        HTMLElement,
        'scrollTop' | 'scrollHeight' | 'clientHeight' | 'offsetHeight' | 'getBoundingClientRect'
    >,
    caret: { top: number; bottom: number },
    margin = 4,
): void {
    if (field.scrollHeight <= field.clientHeight) return
    const box = field.getBoundingClientRect()
    // Viewport px per layout px: a side panel is drawn at 0.85.
    const scale = field.offsetHeight > 0 ? box.height / field.offsetHeight : 1
    if (caret.bottom + margin > box.bottom) {
        field.scrollTop += (caret.bottom + margin - box.bottom) / scale
    } else if (caret.top - margin < box.top) {
        field.scrollTop -= (box.top - (caret.top - margin)) / scale
    }
}
